import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  photosByEntity: {}, // { photoFor: { entityId: [photos] } }
  loading: false,
  error: null,
};

const FETCH_PHOTOS_REQUEST = "FETCH_PHOTOS_REQUEST";
const FETCH_PHOTOS_SUCCESS = "FETCH_PHOTOS_SUCCESS";
const FETCH_PHOTOS_FAILURE = "FETCH_PHOTOS_FAILURE";

const CREATE_PHOTO_SUCCESS = "CREATE_PHOTO_SUCCESS";
const DELETE_PHOTO_SUCCESS = "DELETE_PHOTO_SUCCESS";
const UPDATE_PHOTO_SUCCESS = "UPDATE_PHOTO_SUCCESS";

export const getPhotosByEntityId = (entityId, photoFor) => async (dispatch) => {
  const service = getService(serviceNames.personnelPhotos);
  const query = {
    entityId: entityId,
    photoFor,
    isDeleted: false,
    $sort: { createdAt: -1 },
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: FETCH_PHOTOS_REQUEST,
      success: FETCH_PHOTOS_SUCCESS,
      failure: FETCH_PHOTOS_FAILURE,
    },
    apiCall: () => service.find({ query }),
    transformResult: (photos) => ({
      entityId,
      photoFor,
      photos: photos.map((p) => ({
        ...p,
        entityId: p.entityId,
      })),
    }),
    errorMessagePrefix: "Failed to fetch personnel photos",
  });
};

export const createPhoto = (photoData) => async (dispatch) => {
  const service = getService(serviceNames.personnelPhotos);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: FETCH_PHOTOS_REQUEST,
      success: CREATE_PHOTO_SUCCESS,
      failure: FETCH_PHOTOS_FAILURE,
    },
    apiCall: () => service.create(photoData),
    transformResult: (photo) => ({
      ...photo,
      entityId: photo.entityId,
      photoFor: photoData.photoFor,
    }),
    successMessage: "Photo uploaded successfully",
    errorMessagePrefix: "Failed to upload photo",
  });
};

export const updatePhoto =
  (imageID, entityId, photoFor, updateData, isShow = true) =>
  async (dispatch) => {
    const service = getService(serviceNames.personnelPhotos);

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_PHOTOS_REQUEST,
        success: UPDATE_PHOTO_SUCCESS,
        failure: FETCH_PHOTOS_FAILURE,
      },
      apiCall: () => service.patch(imageID, updateData),
      transformResult: (photo) => ({
        ...photo,
        entityId: photo.entityId,
        photoFor,
      }),
      successMessage: isShow ? "Photo updated successfully" : "",
      errorMessagePrefix: "Failed to update photo",
    });
  };

export const deletePhoto =
  (imageID, entityId, photoFor, userId) => async (dispatch) => {
    const service = getService(serviceNames.personnelPhotos);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedDateTime: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_PHOTOS_REQUEST,
        success: DELETE_PHOTO_SUCCESS,
        failure: FETCH_PHOTOS_FAILURE,
      },
      apiCall: () => service.patch(imageID, patchData),
      transformResult: () => ({
        imageID,
        entityId: entityId,
        photoFor,
      }),
      successMessage: "Photo deleted successfully",
      errorMessagePrefix: "Failed to delete photo",
    });
  };

export default function personnelPhotosReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_PHOTOS_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_PHOTOS_SUCCESS: {
      const { photoFor, entityId, photos } = action.payload;
      return {
        ...state,
        loading: false,
        photosByEntity: {
          ...state.photosByEntity,
          [photoFor]: {
            ...state.photosByEntity[photoFor],
            [entityId]: photos,
          },
        },
      };
    }

    case CREATE_PHOTO_SUCCESS: {
      const { photoFor, entityId } = action.payload;
      const existing = state.photosByEntity[photoFor]?.[entityId] ?? [];
      return {
        ...state,
        loading: false,
        photosByEntity: {
          ...state.photosByEntity,
          [photoFor]: {
            ...state.photosByEntity[photoFor],
            [entityId]: [...existing, action.payload],
          },
        },
      };
    }

    case UPDATE_PHOTO_SUCCESS: {
      const { photoFor, entityId, imageID } = action.payload;
      const updated =
        state.photosByEntity[photoFor]?.[entityId]?.map((p) =>
          p.imageID === imageID ? action.payload : p
        ) ?? [];
      return {
        ...state,
        loading: false,
        photosByEntity: {
          ...state.photosByEntity,
          [photoFor]: {
            ...state.photosByEntity[photoFor],
            [entityId]: updated,
          },
        },
      };
    }

    case DELETE_PHOTO_SUCCESS: {
      const { photoFor, entityId, imageID } = action.payload;
      const filtered =
        state.photosByEntity[photoFor]?.[entityId]?.filter(
          (p) => p.imageID !== imageID
        ) ?? [];
      return {
        ...state,
        loading: false,
        photosByEntity: {
          ...state.photosByEntity,
          [photoFor]: {
            ...state.photosByEntity[photoFor],
            [entityId]: filtered,
          },
        },
      };
    }

    case FETCH_PHOTOS_FAILURE:
      return { ...state, loading: false, error: action.payload };

    default:
      return state;
  }
}
